import { useEffect, useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { createPhotoWorkflow, deletePhotoWorkflow, getPhotoWorkflow, orderPhotoWorkflows, setListItems } from "./photo-workflow.slice";
import { arrayMove } from "@dnd-kit/sortable";
import { uuid } from "@/interfaces";
import { PhotoWorkflow } from "@/models/photo-workflow.model";
import { DragEndEvent } from "@dnd-kit/core";
import { toast } from "sonner";

export function usePhotoWorkflow(id: uuid) {
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((state) => state.photoWorkflow);
  const [add, setAdd] = useState(false);
  const [del, setDel] = useState(false);
  const [selectedId, setSelectedId] = useState(null as uuid | null);

  function load() {
    dispatch(getPhotoWorkflow(id));
  }

  useEffect(() => {
    load();
  }, [id]);

  const setList = useCallback(
    (newList: PhotoWorkflow[]) => {
      dispatch(setListItems([...newList]));
    },
    [dispatch]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = list.findIndex((i) => i.id === active.id);
      const newIndex = list.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(list, oldIndex, newIndex).map((m, i) => ({ ...m, order: i + 1 }));

      setList(reordered);
      dispatch(orderPhotoWorkflows({ service: id, slots: reordered.map((slot) => slot.id) }))
        .unwrap()
        .catch(() => {
          load();
          toast.error("Oops!!", {
            description: "Something went wrong while saving the photo workflow order",
          });
        });
    },
    [list, setList, dispatch, id]
  );

  async function createPhotoWorkflowItem(values: Partial<PhotoWorkflow>) {
    try {
      await dispatch(createPhotoWorkflow({ service: id, title: values.title ?? "", description: values.description })).unwrap();
      setAdd(false);
      toast.success("Success", {
        description: "Photo workflow successfully added",
      });
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while adding photo workflow ",
      });
    }
  }

  async function deletePhotoWorkflowItem() {
    try {
      if (selectedId) {
        await dispatch(deletePhotoWorkflow({ service: id, slot: selectedId })).unwrap();
        setSelectedId(null);
        setDel(false);
        toast.success("Success", {
          description: "Photo workflow successfully deleted",
        });
      }
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while deleting photo workflow ",
      });
    }
  }

  function handleDeleteOpen(id: uuid) {
    setSelectedId(id);
    setDel(true);
  }

  return {
    list,
    loading,
    setList,
    handleDragEnd,
    createPhotoWorkflowItem,
    addDialog: add,
    setAddDialog: setAdd,
    delDialog: del,
    setDelDialog: setDel,
    deletePhotoWorkflow,
    selectedId,
    setSelectedId,
    deletePhotoWorkflowItem,
    handleDeleteOpen,
  };
}
